import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useResolveCallout } from '@/hooks/useResolveCallout';
import { userIdAtom } from '@/services/globalState';
import {
  getUserCalloutsQueryOptions,
  selectCallout,
  shouldSuppressCallout,
  type CalloutKey,
} from '@/services/userCallouts';

export type ComposerCalloutCandidate = {
  key: CalloutKey;
  /** Caller-side eligibility (role, inquiry state). Ineligible candidates are skipped. */
  enabled: boolean;
};

/**
 * Picks the single composer callout to surface when several may be eligible at
 * once, enforcing "one at a time, most recent wins": candidates are passed
 * newest-first and the first that is eligible and unresolved becomes `activeKey`.
 * This is what lets two intros share the same "double your honorarium" promise —
 * the expert only ever sees one. To add a future composer callout, insert it at
 * the position matching its recency (newer = earlier in the array).
 *
 * Visibility uses the same fail-closed gate as `useCalloutBanner`
 * (`shouldSuppressCallout`), so nothing surfaces while the callouts query is
 * loading or errored. `dismiss` resolves `activeKey` through the shared optimistic
 * resolve and is a no-op when nothing is active.
 */
export function useComposerCalloutBanner(candidates: ComposerCalloutCandidate[]) {
  const userId = useAtomValue(userIdAtom);

  const { data: callouts, isSuccess: calloutsLoaded } = useQuery(
    getUserCalloutsQueryOptions(userId)
  );

  const activeKey =
    candidates.find(
      (candidate) =>
        candidate.enabled &&
        !shouldSuppressCallout(calloutsLoaded, selectCallout(callouts, candidate.key))
    )?.key ?? null;

  const resolve = useResolveCallout();

  const dismiss = useCallback(() => {
    if (activeKey) {
      resolve(activeKey);
    }
  }, [activeKey, resolve]);

  return { activeKey, dismiss };
}
